import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { priorityColor, statusColor, statusLabel, STATUS_OPTIONS, CLOSED_STATUSES, PAUSED_STATUSES, timeUntil, humanTime, formatDue, formatDuration, fmtIST } from "@/lib/utils";
import { Plus, MagnifyingGlass, WarningOctagon, ArrowBendUpLeft, CaretDown, CaretUpDown, DownloadSimple, Eye, EyeSlash, ArrowClockwise, Bell, BellSlash, DesktopTower, Prohibit, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = STATUS_OPTIONS.map(o => o.value);
const PRIORITIES = ["P1", "P2", "P3", "P4"];
const TYPES = ["Incident", "Service Request", "Proactive Tasks", "Security Incident"];
const QUICK_LABELS = { open: "Open tickets", unassigned: "Unassigned", breached: "SLA breached", resolved: "Resolved / closed", responded: "Requestor responded", on_hold: "On hold" };

export default function Incidents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const quick = searchParams.get("view");
  const [incidents, setIncidents] = useState([]);
  const [filters, setFilters] = useState(() => ({
    status: localStorage.getItem("trak_inc_status") || "all",
    priority: localStorage.getItem("trak_inc_priority") || "all",
    type: searchParams.get("type") || localStorage.getItem("trak_inc_type") || "all",
    category: localStorage.getItem("trak_inc_category") || "all",
    assignee_id: searchParams.get("assignee_id") || localStorage.getItem("trak_inc_assignee") || "all",
    q: "",
  }));
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [slaView, setSlaView] = useState(() => localStorage.getItem("trak_sla_view") || "due");
  const setSlaViewPref = (v) => { setSlaView(v); localStorage.setItem("trak_sla_view", v); };
  const [sortByDue, setSortByDue] = useState(false);
  const { user } = useAuth();
  const [teamOnly, setTeamOnly] = useState(() => localStorage.getItem("trak_inc_scope") === "team");
  useEffect(() => { localStorage.setItem("trak_inc_scope", teamOnly ? "team" : "all"); }, [teamOnly]);
  const [selected, setSelected] = useState(new Set());
  const [markingSpam, setMarkingSpam] = useState(false);
  const baselineIds = useRef(null);   // ids known at last manual load / dismissal
  const [newCount, setNewCount] = useState(0);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("trak_new_ticket_sound") === "1");
  const toggleSound = () => setSoundOn(v => { localStorage.setItem("trak_new_ticket_sound", v ? "0" : "1"); return !v; });
  const [desktopOn, setDesktopOn] = useState(() => localStorage.getItem("trak_desktop_notif") === "1");
  const toggleDesktop = async () => {
    if (desktopOn) { localStorage.setItem("trak_desktop_notif", "0"); setDesktopOn(false); return; }
    if (!("Notification" in window)) { toast.error("This browser doesn't support desktop notifications"); return; }
    let perm = Notification.permission;
    if (perm === "default") perm = await Notification.requestPermission();
    if (perm === "granted") { localStorage.setItem("trak_desktop_notif", "1"); setDesktopOn(true); toast.success("Desktop notifications on"); }
    else toast.error("Notification permission blocked — enable it in your browser settings");
  };
  const notifyDesktop = (addedRows) => {
    try {
      if (!("Notification" in window) || Notification.permission !== "granted") return;
      const n = addedRows.length;
      const first = addedRows[0] || {};
      const title = n === 1 ? `New ticket: ${first.number || ""}` : `${n} new tickets in TRAK`;
      const body = n === 1 ? (first.title || "") : `Latest: ${first.number || ""} — ${first.title || ""}`;
      const note = new Notification(title, { body, tag: "trak-new-tickets", renotify: true });
      note.onclick = () => { window.focus(); note.close(); };
    } catch { /* ignore */ }
  };

  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const beep = (freq, start, dur) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur + 0.02);
      };
      beep(880, 0, 0.18); beep(1320, 0.16, 0.22);
      setTimeout(() => ctx.close(), 700);
    } catch { /* audio may be blocked until first interaction */ }
  };

  // Reflect unread arrivals in the browser tab title so agents notice from another tab.
  useEffect(() => {
    const base = "Ticket Queue · TRAK";
    document.title = newCount > 0 ? `(${newCount}) new · ${base}` : base;
    return () => { document.title = base; };
  }, [newCount]);

  // Remember the queue filters per agent across visits.
  useEffect(() => {
    localStorage.setItem("trak_inc_status", filters.status);
    localStorage.setItem("trak_inc_priority", filters.priority);
    localStorage.setItem("trak_inc_type", filters.type);
    localStorage.setItem("trak_inc_category", filters.category);
    localStorage.setItem("trak_inc_assignee", filters.assignee_id);
  }, [filters.status, filters.priority, filters.type, filters.category, filters.assignee_id]);

  useEffect(() => {
    api.get("/users").then(r => setStaff((r.data || []).filter(u => ["agent", "manager", "admin"].includes(u.role)))).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const dueSortVal = (i) => {
    if (i.status === "resolved" || i.status === "closed" || i.status === "closed_spam") return Number.POSITIVE_INFINITY;
    if (i.sla_biz_left_seconds != null) return i.sla_biz_left_seconds;  // business seconds remaining (neg=overdue)
    if (i.sla_resolution_due_at) return (new Date(i.sla_resolution_due_at).getTime() - Date.now()) / 1000;
    return Number.POSITIVE_INFINITY;
  };
  const displayed = sortByDue ? [...incidents].sort((a, b) => dueSortVal(a) - dueSortVal(b)) : incidents;

  const load = (isAuto = false) => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.type !== "all") params.type = filters.type;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.assignee_id !== "all") params.assignee_id = filters.assignee_id;
    if (filters.q) params.q = filters.q;
    if (quick === "unassigned") params.assignee_id = "unassigned";
    if (user?.role === "admin" && teamOnly) params.scope = "team";
    api.get("/incidents", { params }).then(r => {
      let rows = r.data;
      const isOpen = (i) => !CLOSED_STATUSES.includes(i.status);
      if (quick === "open" || quick === "unassigned") rows = rows.filter(isOpen);
      if (quick === "on_hold") rows = rows.filter(i => i.status === "on_hold");
      if (quick === "breached") rows = rows.filter(i => isOpen(i) && i.sla_breached);
      if (quick === "resolved") rows = rows.filter(i => !isOpen(i));
      if (quick === "responded") rows = rows.filter(i => isOpen(i) && i.requester_responded);
      // Hide resolved/closed by default unless the user opts in, an explicit status is chosen,
      // or a quick-view that surfaces them is active.
      if (!showClosed && quick !== "resolved" && !CLOSED_STATUSES.includes(filters.status)) {
        rows = rows.filter(isOpen);
      }
      // Track new arrivals for the live banner. Non-auto loads (mount / filter change / dismiss)
      // re-baseline; auto-refreshes count ids not seen at the baseline.
      if (isAuto && baselineIds.current) {
        const addedRows = rows.filter(x => !baselineIds.current.has(x.id));
        if (addedRows.length > 0) {
          setNewCount(addedRows.length);
          if (soundOn) playChime();
          if (desktopOn) notifyDesktop(addedRows);
        }
      } else {
        baselineIds.current = new Set(rows.map(x => x.id));
        setNewCount(0);
      }
      setIncidents(rows);
    });
  };
  const dismissNew = () => { baselineIds.current = new Set(incidents.map(x => x.id)); setNewCount(0); };
  const setCategory = async (incId, category) => {
    try {
      const { data } = await api.patch(`/incidents/${incId}`, { category });
      setIncidents(prev => prev.map(x => x.id === incId ? { ...x, category: data.category } : x));
      toast.success("Category updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  useEffect(() => { setSelected(new Set()); }, [filters, quick, showClosed]);
  const toggleSel = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const visibleIds = displayed.map(x => x.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(visibleIds));
  const bulkSpam = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} ticket(s) as Closed-Spam? They will be closed and excluded from SLA & performance metrics.`)) return;
    setMarkingSpam(true);
    try {
      const { data } = await api.post("/incidents/bulk-spam", { ids });
      toast.success(`${data.updated} ticket(s) marked as Closed-Spam`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to mark as spam"); }
    finally { setMarkingSpam(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 30000);  // silent auto-refresh so externally-created tickets appear live
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, quick, showClosed, soundOn, desktopOn, teamOnly]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.priority !== "all") params.set("priority", filters.priority);
      if (filters.type !== "all") params.set("type", filters.type);
      if (filters.assignee_id !== "all") params.set("assignee_id", filters.assignee_id);
      if (filters.q) params.set("q", filters.q);
      const token = localStorage.getItem("helix_token");
      const res = await fetch(`${API}/incidents/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url;
      a.download = `incidents-${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported to Excel");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const clearQuick = () => setSearchParams({});

  return (
    <div className="p-6" data-testid="incidents-view">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Incident Management</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Ticket Queue</h1>
          {quick && QUICK_LABELS[quick] && (
            <button data-testid="quick-filter-chip" onClick={clearQuick} className="mt-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-sm border border-primary/50 text-primary hover:bg-primary/10 transition-colors">
              {QUICK_LABELS[quick]} <span className="text-muted-foreground">✕</span>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <div className="flex items-center gap-1 border border-border rounded-sm p-0.5 h-10" data-testid="inc-scope-toggle">
              {[[false, "All Members"], [true, "My Team"]].map(([val, label]) => (
                <button key={label} data-testid={`inc-scope-${val ? "team" : "all"}`} onClick={() => setTeamOnly(val)}
                  className={`px-2.5 h-8 text-[11px] font-mono uppercase tracking-wider rounded-sm transition-colors ${teamOnly === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/40"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <Button data-testid="toggle-ticket-sound" onClick={toggleSound} variant="outline" title={soundOn ? "New-ticket chime: on" : "New-ticket chime: off"}
            className={`rounded-sm h-10 w-10 p-0 border-border ${soundOn ? "text-primary border-primary/50" : "text-muted-foreground"}`}>
            {soundOn ? <Bell size={16} weight="fill" /> : <BellSlash size={16} />}
          </Button>
          <Button data-testid="toggle-desktop-notif" onClick={toggleDesktop} variant="outline" title={desktopOn ? "Desktop notifications: on" : "Desktop notifications: off"}
            className={`rounded-sm h-10 w-10 p-0 border-border ${desktopOn ? "text-primary border-primary/50" : "text-muted-foreground"}`}>
            <DesktopTower size={16} weight={desktopOn ? "fill" : "regular"} />
          </Button>
          <Button data-testid="export-incidents-btn" onClick={exportExcel} disabled={exporting} variant="outline" className="rounded-sm font-mono uppercase tracking-wider text-xs h-10 border-border">
            <DownloadSimple size={16} className="mr-2" /> {exporting ? "Exporting…" : "Export Excel"}
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-incident-btn" className="rounded-sm font-mono uppercase tracking-wider text-xs h-10">
                <Plus size={16} className="mr-2" /> New Incident
              </Button>
            </SheetTrigger>
            <NewIncidentSheet onCreated={() => { setOpen(false); load(); }} />
          </Sheet>
        </div>
      </div>

      <Card className="p-4 border-border rounded-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-incidents"
              placeholder="Search title, number, description…"
              className="pl-9 h-10 rounded-sm font-mono text-sm"
              value={filters.q}
              onChange={(e)=>setFilters({...filters, q: e.target.value})}
            />
          </div>
          <Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status: v})}>
            <SelectTrigger data-testid="filter-status" className="rounded-sm font-mono text-xs uppercase"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.priority} onValueChange={(v)=>setFilters({...filters, priority: v})}>
            <SelectTrigger data-testid="filter-priority" className="rounded-sm font-mono text-xs uppercase"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.type} onValueChange={(v)=>setFilters({...filters, type: v})}>
            <SelectTrigger data-testid="filter-type" className="rounded-sm font-mono text-xs uppercase"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v)=>setFilters({...filters, category: v})}>
            <SelectTrigger data-testid="filter-category" className="rounded-sm font-mono text-xs uppercase"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="uncategorized" data-testid="filter-category-uncategorized">Uncategorized</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
            <PopoverTrigger asChild>
              <button data-testid="filter-assignee" role="combobox" aria-expanded={assigneeOpen}
                className="flex items-center justify-between gap-2 rounded-sm border border-input bg-background px-3 h-10 font-mono text-xs uppercase min-w-[11rem] hover:bg-accent/40 transition-colors">
                <span className="truncate">
                  {filters.assignee_id === "all" ? "All assignees"
                    : filters.assignee_id === "unassigned" ? "Unassigned"
                    : (staff.find(u => u.id === filters.assignee_id)?.name || "Assignee")}
                </span>
                <CaretUpDown size={13} className="opacity-60 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-64 rounded-sm" align="start">
              <Command>
                <CommandInput placeholder="Search agent…" data-testid="filter-assignee-search" className="text-xs" />
                <CommandList>
                  <CommandEmpty>No agent found.</CommandEmpty>
                  <CommandGroup>
                    {[{ id: "all", name: "All assignees" }, { id: "unassigned", name: "Unassigned" }, ...staff].map(u => (
                      <CommandItem key={u.id} value={u.name} data-testid={`filter-assignee-opt-${u.id}`}
                        onSelect={() => { setFilters(f => ({ ...f, assignee_id: u.id })); setAssigneeOpen(false); }}>
                        <Check size={13} className={`mr-2 ${filters.assignee_id === u.id ? "opacity-100 text-primary" : "opacity-0"}`} />
                        <span className="text-xs">{u.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center justify-end gap-3 text-xs font-mono text-muted-foreground mt-3">
            <button data-testid="toggle-show-closed" onClick={() => setShowClosed(v => !v)} title="Show/hide resolved & closed tickets"
              className={`flex items-center gap-1.5 px-2 py-1.5 border rounded-sm uppercase tracking-wider transition-colors ${showClosed ? "border-primary text-primary bg-primary/10" : "border-border hover:bg-accent/40"}`}>
              {showClosed ? <Eye size={13} /> : <EyeSlash size={13} />} {showClosed ? "Closed shown" : "Show closed"}
            </button>
            <div className="flex items-center border border-border rounded-sm overflow-hidden" data-testid="sla-view-toggle" title="SLA display preference">
              <button data-testid="sla-view-due" onClick={() => setSlaViewPref("due")}
                className={`px-2 py-1 uppercase tracking-wider transition-colors ${slaView === "due" ? "bg-primary text-primary-foreground" : "hover:bg-accent/40"}`}>Due date</button>
              <button data-testid="sla-view-remaining" onClick={() => setSlaViewPref("remaining")}
                className={`px-2 py-1 uppercase tracking-wider transition-colors ${slaView === "remaining" ? "bg-primary text-primary-foreground" : "hover:bg-accent/40"}`}>Time left</button>
            </div>
            <span className="px-2 py-1 border border-border whitespace-nowrap">{incidents.length} tickets</span>
        </div>
      </Card>

      {newCount > 0 && (
        <button data-testid="new-tickets-banner" onClick={dismissNew}
          className="w-full mb-3 flex items-center justify-center gap-2 rounded-sm border border-primary/40 bg-primary/10 text-primary px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-primary/20 transition-colors animate-in fade-in slide-in-from-top-1">
          <ArrowClockwise size={14} weight="bold" />
          {newCount} new ticket{newCount > 1 ? "s" : ""} arrived — click to dismiss
        </button>
      )}

      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="w-full mb-3 flex items-center gap-3 rounded-sm border border-rag-red/40 bg-rag-red-bg px-4 py-2.5 animate-in fade-in slide-in-from-top-1">
          <span className="text-xs font-mono uppercase tracking-wider text-rag-red-fg">{selected.size} selected</span>
          <Button data-testid="bulk-mark-spam-btn" onClick={bulkSpam} disabled={markingSpam}
            className="h-8 rounded-sm font-mono uppercase text-xs bg-rag-red hover:bg-rag-red/90 text-background">
            <Prohibit size={13} weight="bold" className="mr-1.5" /> {markingSpam ? "Marking…" : "Mark as Spam"}
          </Button>
          <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())}
            className="ml-auto text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1">
            <X size={13} /> Clear
          </button>
        </div>
      )}

      <Card className="border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono border-b border-border">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" data-testid="bulk-select-all" checked={allSelected} onChange={toggleAll}
                    className="h-3.5 w-3.5 accent-rose-600 cursor-pointer" title="Select all visible" />
                </th>
                <th className="text-left px-4 py-3">Ticket</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Prio</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assignee</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">
                  <button data-testid="sla-sort-header" onClick={() => setSortByDue(v => !v)}
                    className={`flex items-center gap-1 uppercase tracking-widest transition-colors hover:text-foreground ${sortByDue ? "text-primary" : ""}`}
                    title="Sort by soonest due first">
                    SLA {sortByDue ? <CaretDown size={11} weight="bold" /> : <CaretUpDown size={11} />}
                  </button>
                </th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(i => {
                const sla = timeUntil(i.sla_resolution_due_at);
                const closed = i.status === 'resolved' || i.status === 'closed' || i.status === 'closed_spam';
                const paused = PAUSED_STATUSES.includes(i.status);
                const overdue = !closed && !paused && sla.breach;
                const biz = i.sla_biz_left_seconds;  // set for business-hours SLAs
                const timeLeft = biz != null ? formatDuration(biz) : sla.text;
                const slaVal = slaView === "due" ? formatDue(i.sla_resolution_due_at) : timeLeft;
                return (
                  <tr key={i.id} data-testid={`ticket-row-${i.number}`} className={`border-b border-border/60 ${selected.has(i.id) ? 'bg-rag-red/[0.08]' : overdue ? 'bg-rag-red-bg hover:bg-rag-red/10' : 'hover:bg-accent/20'}`}>
                    <td className="px-4 py-3 w-8">
                      <input type="checkbox" data-testid={`bulk-select-${i.number}`} checked={selected.has(i.id)} onChange={() => toggleSel(i.id)}
                        className="h-3.5 w-3.5 accent-rose-600 cursor-pointer" />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/incidents/${i.id}`} data-testid={`ticket-link-${i.number}`} className="text-primary hover:underline">{i.number}</Link>
                    </td>
                    <td className="px-4 py-3 max-w-md truncate">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate">{i.title}</span>
                        {i.alert_occurrences > 1 && (
                          <span data-testid={`alert-occurrences-${i.number}`} className="shrink-0 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm border border-rag-amber/40 bg-rag-amber-bg text-rag-amber-fg" title={`Alert recurred ${i.alert_occurrences} times`}>×{i.alert_occurrences}</span>
                        )}
                      </div>
                      {i.requester_responded && (
                        <span data-testid={`requester-responded-${i.number}`} className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono font-bold uppercase tracking-wide text-rag-red-fg">
                          <ArrowBendUpLeft size={11} weight="bold" /> Requestor Responded
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground whitespace-nowrap">{i.type || "Incident"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div>{i.customer_name || <span className="text-muted-foreground">—</span>}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{i.requester_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono px-2 py-0.5 border ${priorityColor(i.priority)}`}>{i.priority}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${statusColor(i.status)}`}>{statusLabel(i.status)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{i.assignee_name || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <CategoryCell inc={i} categories={categories} onChange={setCategory} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap" data-testid={`sla-cell-${i.number}`}>
                      {i.sla_breached ? (
                        <span className="text-rag-red-fg flex items-center gap-1"><WarningOctagon size={12} /> {closed ? 'Breached' : slaVal}</span>
                      ) : closed ? (
                        <span className="text-rag-green-fg">met</span>
                      ) : paused ? (
                        <span className="text-muted-foreground">Paused</span>
                      ) : sla.breach ? (
                        <span className="text-rag-red-fg flex items-center gap-1"><WarningOctagon size={12} /> {slaVal}</span>
                      ) : (
                        <span className="text-rag-amber-fg">{slaVal}</span>
                      )}
                      {slaView === "remaining" && biz != null && !closed && !paused && !i.sla_breached && <span className="ml-1 text-[9px] text-muted-foreground uppercase">biz</span>}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground" title={fmtIST(i.created_at)}>{humanTime(i.created_at)}</td>
                  </tr>
                );
              })}
              {incidents.length === 0 && (
                <tr><td colSpan={11} className="text-center py-8 text-muted-foreground text-sm">No tickets match filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function CategoryCell({ inc, categories, onChange }) {
  const cat = inc.category || "";
  const isUncat = !cat || cat === "Uncategorized";
  const opts = (!cat || categories.includes(cat)) ? categories : [cat, ...categories];
  return (
    <Select value={cat || "__uncat"} onValueChange={(v)=>onChange(inc.id, v === "__uncat" ? "" : v)}>
      <SelectTrigger data-testid={`inline-category-${inc.number}`}
        className={`h-7 w-[132px] rounded-sm text-xs font-mono ${isUncat ? "border-rag-amber/50 text-rag-amber-fg" : "text-muted-foreground"}`}>
        <SelectValue placeholder="Uncategorized" />
      </SelectTrigger>
      <SelectContent>
        {!cat && <SelectItem value="__uncat">Uncategorized</SelectItem>}
        {opts.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const UNASSIGNED = "__unassigned";
const REQUIRED_FIELDS = [["title", "Title"], ["description", "Description"], ["type", "Type"], ["priority", "Priority"], ["category", "Category"], ["customer_name", "Client"], ["requester_email", "Requester email"], ["source", "Logged via"]];

function NewIncidentSheet({ onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", type: "", priority: "", category: "", requester_email: "", customer_name: "", source: "" });
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [categories, setCategories] = useState(["General"]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/clients").then(r => setClients(r.data)).catch(() => {});
  }, []);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.requester_email.trim());
  const missing = REQUIRED_FIELDS.filter(([k]) => !String(form[k] || "").trim()).map(([, label]) => label);
  if (form.requester_email.trim() && !emailOk) missing.push("Requester email (invalid)");
  const invalid = (k) => touched && !String(form[k] || "").trim();
  const errCls = (k) => invalid(k) ? "border-rag-red-fg" : "";
  const Req = ({ k, children }) => <Label className={`text-xs uppercase tracking-widest ${invalid(k) ? "text-rag-red-fg" : "text-muted-foreground"}`}>{children} <span className="text-rag-red-fg">*</span></Label>;
  const sel = (k) => ({ value: form[k] || UNASSIGNED, onValueChange: (v) => setForm({ ...form, [k]: v === UNASSIGNED ? "" : v }) });

  const submit = async () => {
    setTouched(true);
    if (missing.length) { toast.error(`Please complete: ${missing.join(", ")}`); return; }
    setSaving(true);
    try {
      await api.post("/incidents", form);
      toast.success("Incident created");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <SheetContent className="bg-background border-border w-full sm:max-w-lg overflow-y-auto">
      <SheetHeader>
        <SheetTitle className="font-display">New Incident</SheetTitle>
        <SheetDescription>Log a new ITIL incident. All fields are mandatory; SLA timers start on creation.</SheetDescription>
      </SheetHeader>
      <div className="space-y-4 mt-6">
        <div>
          <Req k="title">Title</Req>
          <Input data-testid="new-title" aria-invalid={invalid("title")} className={`mt-1 rounded-sm ${errCls("title")}`} value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} />
        </div>
        <div>
          <Req k="description">Description</Req>
          <Textarea data-testid="new-description" aria-invalid={invalid("description")} className={`mt-1 rounded-sm min-h-[120px] ${errCls("description")}`} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} />
        </div>
        <div>
          <Req k="type">Type</Req>
          <Select {...sel("type")}>
            <SelectTrigger data-testid="new-type" aria-invalid={invalid("type")} className={`mt-1 rounded-sm ${errCls("type")} ${!form.type ? "text-muted-foreground" : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED} disabled>Unassigned</SelectItem>
              {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Req k="priority">Priority</Req>
            <Select {...sel("priority")}>
              <SelectTrigger data-testid="new-priority" aria-invalid={invalid("priority")} className={`mt-1 rounded-sm ${errCls("priority")} ${!form.priority ? "text-muted-foreground" : ""}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED} disabled>Unassigned</SelectItem>
                {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Req k="category">Category</Req>
            <Select {...sel("category")}>
              <SelectTrigger data-testid="new-category" aria-invalid={invalid("category")} className={`mt-1 rounded-sm ${errCls("category")} ${!form.category ? "text-muted-foreground" : ""}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED} disabled>Unassigned</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Req k="customer_name">Client</Req>
          <Select {...sel("customer_name")}>
            <SelectTrigger data-testid="new-customer" aria-invalid={invalid("customer_name")} className={`mt-1 rounded-sm ${errCls("customer_name")} ${!form.customer_name ? "text-muted-foreground" : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED} disabled>Unassigned</SelectItem>
              {clients.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Req k="requester_email">Requester email</Req>
          <Input data-testid="new-requester" type="email" placeholder="Unassigned — enter the requester's email" aria-invalid={invalid("requester_email") || (touched && !emailOk)} className={`mt-1 rounded-sm font-mono ${errCls("requester_email")} ${touched && form.requester_email && !emailOk ? "border-rag-red-fg" : ""}`} value={form.requester_email} onChange={(e)=>setForm({...form, requester_email: e.target.value})} />
        </div>
        <div>
          <Req k="source">Logged via</Req>
          <div className={`mt-1 grid grid-cols-4 gap-2 ${invalid("source") ? "p-1 -m-1 rounded-sm ring-1 ring-rag-red-fg" : ""}`} data-testid="new-source-group">
            {["portal","phone","chat","email"].map(s => (
              <button
                key={s}
                type="button"
                data-testid={`new-source-${s}`}
                aria-pressed={form.source === s}
                onClick={()=>setForm({...form, source: s})}
                className={`text-[11px] font-mono uppercase tracking-wider py-2 rounded-sm border transition-colors ${form.source === s ? 'border-primary text-primary bg-primary/10' : 'border-border text-muted-foreground hover:text-foreground'}`}
              >{s}</button>
            ))}
          </div>
          {!form.source && <div className="text-[10px] font-mono text-muted-foreground mt-1">Unassigned — pick how this was reported</div>}
        </div>
        {touched && missing.length > 0 && <div data-testid="new-incident-missing" className="text-xs text-rag-red-fg border border-rag-red/40 bg-rag-red-bg rounded-sm px-3 py-2">Required: {missing.join(", ")}</div>}
        <Button data-testid="create-incident-submit" onClick={submit} disabled={saving || (touched && missing.length > 0)} className="w-full rounded-sm font-mono uppercase tracking-widest">
          {saving ? "Creating…" : missing.length ? `Create Incident (${missing.length} field${missing.length > 1 ? "s" : ""} left)` : "Create Incident"}
        </Button>
      </div>
    </SheetContent>
  );
}
